import {
    AlertFormData,
    AlertRuleActionData,
    AlertRuleActionType,
    AlertRulePrompt,
    FullAlertRule,
} from '@/types/alert';
import i18n from '@/lib/i18n';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ALERT_DEFAULT_CONFIDENCE_THRESHOLD } from '@/constants/alert';
import {
    emptyAuthorization,
    webhookUrlOrigin,
} from '@/features/AlertRuleCreation/validation';

function createDefaultAlertFormData(): AlertFormData {
    return {
        name: undefined,
        sources: [],
        prompt: '',
        attachment: undefined,
        actions: [
            {
                type: AlertRuleActionType.APP_NOTIFICATION,
                confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
            },
        ],
        active: true,
    };
}

export function getAlertRuleDisplayName(
    alertRule?: Pick<FullAlertRule, 'prompts' | 'name'>,
): string {
    if (!alertRule) return '';
    const trimmedPrompt = alertRule.prompts?.[0]?.text?.trim();
    if (trimmedPrompt) {
        return i18n.t(`${I18nNamespace.ALERT_RULES}:displayName`, {
            prompt: trimmedPrompt,
        });
    }
    const trimmedStoredName = alertRule.name?.trim();
    if (trimmedStoredName) {
        return trimmedStoredName;
    }
    return i18n.t(`${I18nNamespace.ALERT_RULES}:unnamedAlert`);
}

function buildAttachment(
    prompt: AlertRulePrompt | undefined,
): AlertFormData['attachment'] {
    const attachment = prompt?.attachments?.[0];
    if (!attachment?.url) return undefined;

    return {
        url: attachment.url,
        type: attachment.type,
    };
}

function buildAction(
    action: AlertRuleActionData,
): AlertFormData['actions'][number] {
    return {
        id: action.id,
        type: action.type,
        confidenceThreshold: action.threshold,
        recipient: action.recipient?.phone
            ? { phone: action.recipient.phone }
            : undefined,
        webhook: action.webhook
            ? {
                  url: action.webhook.url,
                  headers: (action.webhook.headers ?? []).map((header) => ({
                      id: crypto.randomUUID(),
                      name: header.name,
                      value: header.value,
                  })),
                  authorization: action.webhook.authorization
                      ? {
                            type: action.webhook.authorization.type,
                            username:
                                action.webhook.authorization.username ?? '',
                            secret: '',
                            stored: {
                                type: action.webhook.authorization.type,
                                origin:
                                    webhookUrlOrigin(action.webhook.url) ?? '',
                            },
                        }
                      : emptyAuthorization(),
              }
            : undefined,
    };
}

export function createFormDataFromAlertRule(
    alertRule: FullAlertRule | undefined,
): AlertFormData {
    if (!alertRule) return createDefaultAlertFormData();

    const primaryPrompt = alertRule.prompts?.[0];

    return {
        name: alertRule.name,
        sources: alertRule.sources?.map((source) => source.id) ?? [],
        prompt: primaryPrompt?.text ?? '',
        attachment: buildAttachment(primaryPrompt),
        actions: alertRule.actions?.map(buildAction) ?? [],
        active: alertRule.active ?? true,
    };
}
